import React, {
  createContext,
  type FC,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { screenNames } from "clientState/AuthFlow/providers/ScreenProvider/screenNames";
import useIsSocialAuthlDisabled from "clientState/useIsSocialAuthlDisabled";

import { useModalState } from "../ModalScreenProvider";
import type {
  CreateInitialStateFn,
  ScreenNameType,
  ScreenProviderContextType,
  ScreenProviderProps,
} from "./contract";
import { type DispatchType, type StateType } from "./reducer/contract";
import { screenReducer } from "./reducer/screenReducer";

const { AUTH_METHODS, NONE, PASSWORDLESS, CUSTOM_INITIAL, CUSTOM_SUCCESS } =
  screenNames;

export const ScreenContext = createContext<
  ScreenProviderContextType | undefined
>(undefined);

const createInitialState: CreateInitialStateFn = ({
  skipAuthMethods,
  customScreensConfig,
  email = "",
  trackingPrefix,
  gaTrackingPrefix,
}) => {
  const hasCustomInitialScreen = !!customScreensConfig.initialScreen?.component;

  let initialScreen: ScreenNameType = skipAuthMethods
    ? PASSWORDLESS
    : AUTH_METHODS;
  if (hasCustomInitialScreen) {
    initialScreen = CUSTOM_INITIAL;
  }

  const successScreen = customScreensConfig.successScreen?.component
    ? CUSTOM_SUCCESS
    : NONE;

  return {
    initialScreen,
    previousScreen: NONE,
    screen: initialScreen,
    skipAuthMethods,
    successScreen,
    userData: {
      acceptsNewsletter: false,
      email,
      multiSessionToken: "",
    },
    trackingPrefix,
    gaTrackingPrefix,
  };
};

const ScreenProvider: FC<ScreenProviderProps> = ({
  children,
  gaTrackingPrefix,
}) => {
  const { skipAuthMethods, customScreensConfig, email, origin } =
    useModalState();
  const isSocialDisabled = useIsSocialAuthlDisabled();

  const shouldSkipAuthMethods = skipAuthMethods || isSocialDisabled;

  const initialState = createInitialState({
    skipAuthMethods: shouldSkipAuthMethods,
    customScreensConfig,
    email,
    trackingPrefix: origin,
    gaTrackingPrefix,
  });

  const [state, dispatch] = useReducer(screenReducer, initialState) as [
    StateType,
    DispatchType,
  ];

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <ScreenContext.Provider value={value}>{children}</ScreenContext.Provider>
  );
};

export default ScreenProvider;

export const useScreenState = (): ScreenProviderContextType => {
  const context = useContext(ScreenContext);

  if (context === undefined) {
    throw new Error("Screen Context was used outside of its Provider");
  }

  return context;
};
